import { FC, KeyboardEvent } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';

import {
  Face1Svg,
  Face2Svg,
  Face3Svg,
  Face4Svg,
  Face5Svg,
  Face6Svg,
  Face7Svg,
  Face8Svg,
} from '../../../assets/svg';
import { Answer } from '../../../types';

type SurveyAnswerButtonProps = {
  answer: Answer;
  selected?: boolean;
  onClick: () => void;
};

export const SurveyAnswerButton: FC<SurveyAnswerButtonProps> = ({
  answer,
  selected = false,
  onClick,
}): JSX.Element => {
  const { answer: text, iconId } = answer;

  const getSelectedColor = (): string => {
    switch (iconId) {
      case 1:
        return '#3d7617';
      case 2:
        return '#809300';
      case 3:
        return '#fa7e02';
      case 4:
        return '#fc0808';
      case 5:
        return '#ef1600';
      case 6:
        return '#1e878c';
      case 7:
        return '#1badb4';
      case 8:
        return '#048970';
      default:
        return '#1badb4';
    }
  };

  // Se elige con teclado igual que con el mouse, como cualquier botón.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick();
  };

  // Alto mínimo y no fijo: desde CUDL-074 una respuesta puede tener 200
  // caracteres y la tarjeta crece con ella, como en la app (VWA-38). Con
  // textos cortos mide lo mismo que antes: 89 de la carita y 77 de la etiqueta.
  return (
    <Card
      elevation={4}
      role='button'
      tabIndex={0}
      aria-pressed={selected}
      style={{
        width: 99,
        minHeight: 166,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 8,
        backgroundColor: selected ? getSelectedColor() : '#F8F8F8',
        cursor: 'pointer',
      }}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <Stack flexGrow={1}>
        <Card
          elevation={4}
          sx={{
            width: 99,
            height: 89,
            flexShrink: 0,
            borderRadius: 2,
          }}
        >
          <Box
            display='flex'
            justifyContent='center'
            width='100%'
            height='100%'
            border='6px solid white'
            borderBottom='6px solid white'
            borderRadius='2px'
            sx={{
              backgroundColor: selected ? `${getSelectedColor()}40` : 'white',
            }}
          >
            {(() => {
              switch (iconId) {
                case 1:
                  return <Face1Svg width='42px' />;
                case 2:
                  return <Face2Svg width='42px' />;
                case 3:
                  return <Face3Svg width='42px' />;
                case 4:
                  return <Face4Svg width='42px' />;
                case 5:
                  return <Face5Svg width='42px' />;
                case 6:
                  return <Face6Svg width='42px' />;
                case 7:
                  return <Face7Svg width='42px' />;
                case 8:
                  return <Face8Svg width='42px' />;
                default:
                  return null;
              }
            })()}
          </Box>
        </Card>
        <Typography
          color={selected ? 'white' : getSelectedColor()}
          display='flex'
          justifyContent='center'
          alignItems='center'
          variant='body2'
          sx={{
            // Sube 6 px para tapar el borde inferior de la carita, como antes.
            mt: '-6px',
            flexGrow: 1,
            // El borde cuenta dentro de los 83 px. Antes la etiqueta medía 89 y
            // la tarjeta de 166 le recortaba el borde inferior y el derecho.
            boxSizing: 'border-box',
            minHeight: '83px',
            overflowWrap: 'anywhere',
            border: '3px solid white',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
          }}
        >
          {text}
        </Typography>
      </Stack>
    </Card>
  );
};
